import { doc, setDoc, deleteDoc, getDoc, getDocs, collection, query, orderBy, writeBatch, type Firestore } from 'firebase/firestore';
import {
  courseSchema, chapterSchema, sectionSchema, resourceSchema, toolSchema, siteSettingsSchema,
  toWritable,
  type CourseDoc, type ChapterDoc, type SectionDoc, type ResourceDoc, type ToolDoc, type SiteSettingsDoc
} from './data-model';

const SCHEMAS: Record<string, { schema: { parse: (x: unknown) => unknown }; allowed: string[] }> = {
  courses: { schema: courseSchema, allowed: ['order', 'code', 'name', 'tagline', 'glyph', 'accent', 'tint', 'deep', 'hidden'] },
  chapters: { schema: chapterSchema, allowed: ['courseId', 'order', 'num', 'title', 'blurb', 'hidden'] },
  sections: { schema: sectionSchema, allowed: ['chapterId', 'order', 'title', 'summary', 'hidden'] },
  resources: { schema: resourceSchema, allowed: ['sectionId', 'courseId', 'order', 'kind', 'title', 'meta', 'url', 'youtubeId', 'storagePath', 'fileName', 'hidden'] },
  tools: { schema: toolSchema, allowed: ['order', 'name', 'tag', 'blurb', 'url', 'glyph', 'iconBg', 'iconFg', 'hidden'] },
  siteSettings: { schema: siteSettingsSchema, allowed: ['appName', 'tagline', 'footerText', 'gamified', 'published'] }
};

export type CollName = keyof typeof SCHEMAS;

/** Validate + write a content doc (admin only, enforced again by rules). */
export async function saveDoc(db: Firestore, coll: CollName, id: string, data: unknown): Promise<void> {
  const { schema, allowed } = SCHEMAS[coll];
  const parsed = schema.parse(data); // throws on invalid shape
  const clean = toWritable(parsed as Record<string, unknown>, [...allowed, 'updatedAt']);
  clean.updatedAt = Date.now();
  await setDoc(doc(db, coll, id), clean, { merge: true });
}

export async function removeDoc(db: Firestore, coll: CollName, id: string): Promise<void> {
  await deleteDoc(doc(db, coll, id));
}

/** Load all docs of a collection ordered by order (for reorder UIs). */
export async function orderedDocs<T>(db: Firestore, coll: CollName): Promise<Array<T & { id: string }>> {
  const snap = await getDocs(query(collection(db, coll), orderBy('order')));
  return snap.docs.map(d => ({ ...(d.data() as object), id: d.id }) as T & { id: string });
}

/** Swap order values of two docs atomically. */
export async function swapOrder(db: Firestore, coll: CollName, idA: string, idB: string): Promise<void> {
  const [a, b] = await Promise.all([getDoc(doc(db, coll, idA)), getDoc(doc(db, coll, idB))]);
  const oa = (a.data() as { order: number }).order;
  const ob = (b.data() as { order: number }).order;
  const batch = writeBatch(db);
  batch.update(doc(db, coll, idA), { order: ob, updatedAt: Date.now() });
  batch.update(doc(db, coll, idB), { order: oa, updatedAt: Date.now() });
  await batch.commit();
}

/** Move doc to front/back or swap with neighbour in the ordered list. */
export async function moveDoc(db: Firestore, coll: CollName, id: string, direction: 'up' | 'down'): Promise<void> {
  const docs = await orderedDocs(db, coll);
  const i = docs.findIndex(d => d.id === id);
  if (i < 0) return;
  const j = direction === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= docs.length) return;
  await swapOrder(db, coll, id, docs[j].id);
}

export type { CourseDoc, ChapterDoc, SectionDoc, ResourceDoc, ToolDoc, SiteSettingsDoc };
